import type { FormikHelpers } from 'formik'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'

import { authService } from './auth-service'

// Standardized password validation pattern and messages (matching constants file)
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])/
const PASSWORD_ERROR_MESSAGE =
  'Password must contain at least one uppercase letter, one lowercase letter, one number and one special character'

export interface ForgotPasswordData {
  email: string
}

export interface VerificationCodeData {
  confirmationCode: string
}

export interface NewPasswordData {
  newPassword: string
  confirmPassword: string
}

export interface ResetPasswordData {
  email: string
  confirmationCode: string
  newPassword: string
  confirmPassword: string
}

// Validation schemas
export const forgotPasswordValidationSchema = Yup.object({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required')
    .trim(),
})

export const verificationCodeValidationSchema = Yup.object({
  confirmationCode: Yup.string()
    .required('Verification code is required')
    .min(6, 'Verification code must be at least 6 characters')
    .trim(),
})

export const newPasswordValidationSchema = Yup.object({
  newPassword: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(PASSWORD_REGEX, PASSWORD_ERROR_MESSAGE),
  confirmPassword: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('newPassword')], 'Passwords must match'),
})

export const resetPasswordValidationSchema = Yup.object({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required')
    .trim(),
  confirmationCode: Yup.string()
    .required('Verification code is required')
    .min(6, 'Verification code must be at least 6 characters')
    .trim(),
  newPassword: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(PASSWORD_REGEX, PASSWORD_ERROR_MESSAGE),
  confirmPassword: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('newPassword')], 'Passwords must match'),
})

export const useForgotPassword = () => {
  const navigate = useNavigate()
  const [isCodeSent, setIsCodeSent] = useState(false)
  const [isCodeVerified, setIsCodeVerified] = useState(false)
  const [email, setEmail] = useState('')
  const [confirmationCode, setConfirmationCode] = useState('')

  const handleForgotPassword = async (values: ForgotPasswordData) => {
    try {
      await authService.forgotPassword(values.email)

      setEmail(values.email)
      setIsCodeSent(true)

      navigate('/auth/verification', { state: { email: values.email } })
    } catch (error: any) {
      authService.handleError(error, 'forgotPassword')
    }
  }

  const handleVerifyCode = async (
    email: string,
    values: VerificationCodeData,
    { setSubmitting, setStatus }: FormikHelpers<VerificationCodeData>,
  ) => {
    try {
      setStatus('')
      // TODO: add verifyResetPasswordCode function into auth-service
      // await authService.verifyResetPasswordCode(email, values.confirmationCode)

      setConfirmationCode(values.confirmationCode)
      setIsCodeVerified(true)

      navigate('/auth/reset-password', {
        state: {
          email: email,
          confirmationCode: values.confirmationCode,
        },
      })
    } catch (error: any) {
      console.log('🚀 ~ useForgotPassword ~ error:', error)
      authService.handleError(error, 'confirmResetPassword', setStatus)
    } finally {
      setSubmitting(false)
    }
  }

  const handleSetNewPassword = async (
    email: string,
    confirmationCode: string,
    values: NewPasswordData,
    { setSubmitting, setStatus }: FormikHelpers<NewPasswordData>,
  ) => {
    try {
      setStatus('')
      await authService.confirmResetPassword(email, confirmationCode, values.newPassword)

      setIsCodeSent(false)
      setIsCodeVerified(false)
      setEmail('')

      setConfirmationCode('')

      navigate('/auth/login', {
        replace: true,
        state: {
          message: 'Password reset successfully! Please login with your new password.',
        },
      })
    } catch (error: any) {
      authService.handleError(error, 'confirmResetPassword', setStatus)
    } finally {
      setSubmitting(false)
    }
  }

  const handleResetPassword = async (
    values: ResetPasswordData,
    { setSubmitting, setStatus }: FormikHelpers<ResetPasswordData>,
  ) => {
    try {
      setStatus('')
      await authService.confirmResetPassword(
        values.email,
        values.confirmationCode,
        values.newPassword,
      )

      setIsCodeSent(false)
      setIsCodeVerified(false)
      setEmail('')
      setConfirmationCode('')

      navigate('/auth/login', { replace: true })
    } catch (error: any) {
      authService.handleError(error, 'confirmResetPassword', setStatus)
    } finally {
      setSubmitting(false)
    }
  }

  const handleResendCode = async (email: string) => {
    try {
      await authService.forgotPassword(email)
    } catch (error: any) {
      console.error('Failed to resend code:', error)
      throw error
    }
  }

  const resetState = () => {
    setIsCodeSent(false)
    setIsCodeVerified(false)
    setEmail('')
    setConfirmationCode('')
  }

  return {
    handleForgotPassword,
    handleVerifyCode,
    handleSetNewPassword,
    handleResetPassword,
    handleResendCode,
    resetState,

    isCodeSent,
    isCodeVerified,
    email,
    confirmationCode,

    forgotPasswordValidationSchema,

    verificationCodeValidationSchema,
    newPasswordValidationSchema,
    resetPasswordValidationSchema,
  }
}
